import { useState } from 'react';
import { FocusedAudio } from './FocusedAudio';

const API_BASE = import.meta.env.VITE_API_BASE as string;

export type ToolStatus = 'running' | 'done' | 'error';

export interface ToolOutput {
  id: string;
  name: string;
  status: ToolStatus;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  return (
    <div
      className="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      tabIndex={-1}
    >
      <img className="lightbox-image" src={src} alt={alt} />
    </div>
  );
}

export function ToolCard({ output }: { output: ToolOutput }) {
  const [lightbox, setLightbox] = useState(false);

  if (output.status === 'running') {
    return (
      <div className="tool-card tool-card-running">
        <span className="tool-name">{output.name}</span>
        <span className="tool-status muted">working…</span>
      </div>
    );
  }
  if (output.status === 'error') {
    return (
      <div className="tool-card tool-card-error">
        <span className="tool-name">{output.name}</span>
        <span className="tool-error">{output.error || 'failed'}</span>
      </div>
    );
  }

  const r = output.result ?? {};
  const signedUrl =
    typeof r.signedUrl === 'string' ? absolute(r.signedUrl) : null;
  const title =
    (typeof r.title === 'string' && r.title) ||
    (typeof output.args?.prompt === 'string' &&
      (output.args.prompt as string).slice(0, 60)) ||
    output.name;

  // Stage 25: async-media queued envelope. Returned by generate_image,
  // GPT-image-tool, generate_music, generate_speech (and alias _clip).
  // The asset doesn't exist yet — render a "queued" card that points
  // the user at the appropriate gallery. kling-video-tool also uses
  // this pattern (Stage 22 envelope had mediaType omitted; treat a
  // bare `status:'submitted'` as a video for that case).
  if (r.status === 'submitted') {
    const mediaType =
      typeof r.mediaType === 'string' ? (r.mediaType as string) : 'video';
    const galleryLabel =
      mediaType === 'image'
        ? 'Image Gallery'
        : mediaType === 'music'
          ? 'Music Gallery'
          : mediaType === 'speech'
            ? 'Speech Gallery'
            : 'Video Gallery';
    const icon =
      mediaType === 'image'
        ? '🖼'
        : mediaType === 'music'
          ? '🎵'
          : mediaType === 'speech'
            ? '🔊'
            : '🎬';
    const waitSecs =
      typeof r.estimatedWaitSeconds === 'number'
        ? (r.estimatedWaitSeconds as number)
        : null;
    const waitStr = waitSecs
      ? waitSecs >= 60
        ? `~${Math.max(1, Math.round(waitSecs / 60))} min`
        : `~${waitSecs} s`
      : null;
    const koji = typeof r.koji === 'string' ? (r.koji as string) : null;
    return (
      <div className="tool-card tool-card-queued">
        <div className="tool-card-queued-icon" aria-hidden="true">
          {icon}
        </div>
        <div className="tool-card-queued-body">
          <div className="tool-card-queued-title">{title}</div>
          <div className="muted small">
            Queued — generating in the background. Appears in the{' '}
            {galleryLabel} when ready{waitStr ? ` (${waitStr})` : ''}
            {koji ? ` · ${koji}` : ''}.
          </div>
        </div>
      </div>
    );
  }

  if (output.name === 'generate_image' && signedUrl) {
    return (
      <>
        <div
          className="tool-card tool-card-image"
          onClick={() => setLightbox(true)}
        >
          <img src={signedUrl} alt={title} className="tool-image" />
          <div className="tool-caption muted small">{title}</div>
        </div>
        {lightbox ? (
          <Lightbox
            src={signedUrl}
            alt={title}
            onClose={() => setLightbox(false)}
          />
        ) : null}
      </>
    );
  }

  if (output.name === 'generate_music' && signedUrl) {
    return (
      <div className="tool-card tool-card-music">
        <div className="tool-name">music · {title}</div>
        <FocusedAudio surface="music" controls src={signedUrl} />
      </div>
    );
  }

  if (
    (output.name === 'generate_speech' ||
      output.name === 'generate_speech_clip') &&
    signedUrl
  ) {
    const voice = typeof r.voice === 'string' ? r.voice : 'voice';
    const presetName =
      typeof r.presetName === 'string' && r.presetName ? r.presetName : null;
    return (
      <div className="tool-card tool-card-speech">
        <div className="tool-name">
          speech ·{' '}
          {presetName ? (
            <>
              <strong>🎛 {presetName}</strong>{' '}
              <span className="muted small">· {voice}</span>
            </>
          ) : (
            voice
          )}
        </div>
        <FocusedAudio surface="speech" controls src={signedUrl} />
      </div>
    );
  }

  if (output.name === 'describe_image') {
    const caption = typeof r.caption === 'string' ? r.caption : '';
    const src =
      typeof r.r2Key === 'string'
        ? absolute(`/api/resource/${encodeURIComponent(r.r2Key)}`)
        : null;
    return (
      <div className="tool-card tool-card-caption">
        <div className="tool-name">caption</div>
        {src ? (
          <img src={src} alt="source" className="tool-thumb" />
        ) : null}
        <div className="tool-caption-text">{caption}</div>
      </div>
    );
  }

  return (
    <div className="tool-card">
      <span className="tool-name">{output.name}</span>
      <span className="muted small">done</span>
    </div>
  );
}
